'use client';

import React, { useState, useMemo, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  CITIES, RESOURCE_CATEGORIES, DATA_LAYERS,
  Ward, ResourceItem, City
} from '../muslim-resource-guide/components/cityData';

// ─── EMBED REGISTRY ───────────────────────────────────────────────────────────
// Each embed instance registers itself in a growing collection accessible
// via the public garden gallery and the MRG embed collection page.

interface EmbedInstance {
  city: string;
  ward: string | null;
  theme: 'dark' | 'light' | 'forest';
  compact: boolean;
  backlink: boolean;
}

// ─── RESOURCE CARD ────────────────────────────────────────────────────────────
function ResourceCard({ resource, accentColor }: { resource: ResourceItem; accentColor: string }) {
  const cat = RESOURCE_CATEGORIES.find(c => c.key === resource.category);
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${accentColor}22`,
      borderRadius: 6,
      padding: '12px 14px',
      marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 12, color: '#F5F0E8', fontWeight: 600, marginBottom: 2 }}>{resource.name}</div>
          <div style={{ fontSize: 9, color: accentColor, letterSpacing: '.1em', textTransform: 'uppercase', fontFamily: 'monospace' }}>
            {resource.category} · {resource.nameOfAllah}
          </div>
        </div>
        {cat && (
          <div style={{ fontSize: 9, color: `${accentColor}80`, fontFamily: "'Amiri', serif", fontSize: 16 }}>
            {cat.arabic}
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.6, marginBottom: 6 }}>{resource.desc}</div>
      {resource.address && (
        <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.4)', fontFamily: 'monospace' }}>📍 {resource.address}</div>
      )}
      {resource.contact && (
        <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.4)', fontFamily: 'monospace' }}>📞 {resource.contact}</div>
      )}
    </div>
  );
}

// ─── DISPARITY CARD ───────────────────────────────────────────────────────────
function DisparityCard({ disparity, accentColor }: { disparity: Ward['disparities'][0]; accentColor: string }) {
  return (
    <div style={{
      background: `${accentColor}08`,
      border: `1px solid ${accentColor}20`,
      borderLeft: `3px solid ${accentColor}60`,
      borderRadius: 4,
      padding: '10px 12px',
      marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: '#F5F0E8', fontWeight: 600 }}>{disparity.name}</span>
        <span style={{ fontSize: 12, color: accentColor, fontFamily: 'monospace', fontWeight: 700 }}>{disparity.value}</span>
      </div>
      <div style={{ fontSize: 9, color: `${accentColor}80`, letterSpacing: '.1em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 4 }}>
        {disparity.nameOfAllah} · {disparity.arabicName}
      </div>
      <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', lineHeight: 1.5, fontStyle: 'italic' }}>{disparity.islahCall}</div>
    </div>
  );
}

// ─── BACKLINK FOOTER ──────────────────────────────────────────────────────────
function BacklinkFooter({ city, ward }: { city: string; ward: string | null }) {
  return (
    <div style={{
      borderTop: '1px solid rgba(201,168,76,0.15)',
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
      background: 'rgba(201,168,76,0.03)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: 'rgba(201,168,76,0.6)' }}>✦</span>
        <span style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase' }}>
          The Haal Project · Muslim Resource Guide
        </span>
      </div>
      <a
        href={`https://haalproject.org/muslim-resource-guide?city=${city}${ward ? `&ward=${ward}` : ''}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontSize: 9,
          color: '#c9a84c',
          fontFamily: 'monospace',
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          textDecoration: 'none',
          border: '1px solid rgba(201,168,76,0.3)',
          borderRadius: 3,
          padding: '4px 10px',
          transition: 'all 0.2s',
        }}
      >
        Full Guide → haalproject.org
      </a>
    </div>
  );
}

// ─── CATEGORY FILTER ─────────────────────────────────────────────────────────
function CategoryFilter({
  active,
  onChange,
  accentColor,
}: {
  active: string | null;
  onChange: (cat: string | null) => void;
  accentColor: string;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
      <button
        onClick={() => onChange(null)}
        style={{
          padding: '4px 10px',
          borderRadius: 20,
          border: `1px solid ${active === null ? accentColor : 'rgba(255,255,255,0.1)'}`,
          background: active === null ? `${accentColor}18` : 'transparent',
          color: active === null ? accentColor : 'rgba(212,197,169,0.4)',
          fontSize: 9,
          fontFamily: 'monospace',
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        All
      </button>
      {RESOURCE_CATEGORIES.map(cat => (
        <button
          key={cat.key}
          onClick={() => onChange(active === cat.key ? null : cat.key)}
          style={{
            padding: '4px 10px',
            borderRadius: 20,
            border: `1px solid ${active === cat.key ? accentColor : 'rgba(255,255,255,0.1)'}`,
            background: active === cat.key ? `${accentColor}18` : 'transparent',
            color: active === cat.key ? accentColor : 'rgba(212,197,169,0.4)',
            fontSize: 9,
            fontFamily: 'monospace',
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          {cat.key}
        </button>
      ))}
    </div>
  );
}

// ─── WARD SELECTOR ────────────────────────────────────────────────────────────
function WardSelector({
  city,
  selectedWardId,
  onSelect,
  accentColor,
}: {
  city: City;
  selectedWardId: string | null;
  onSelect: (id: string | null) => void;
  accentColor: string;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.12em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 8 }}>
        Select Ward / Neighborhood
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <button
          onClick={() => onSelect(null)}
          style={{
            padding: '5px 12px',
            borderRadius: 4,
            border: `1px solid ${selectedWardId === null ? accentColor : 'rgba(255,255,255,0.1)'}`,
            background: selectedWardId === null ? `${accentColor}15` : 'transparent',
            color: selectedWardId === null ? accentColor : 'rgba(212,197,169,0.4)',
            fontSize: 10,
            fontFamily: 'monospace',
            cursor: 'pointer',
          }}
        >
          All Wards
        </button>
        {city.wards.map(ward => (
          <button
            key={ward.id}
            onClick={() => onSelect(ward.id === selectedWardId ? null : ward.id)}
            style={{
              padding: '5px 12px',
              borderRadius: 4,
              border: `1px solid ${selectedWardId === ward.id ? accentColor : `rgba(255,255,255,${ward.severity === 'CRITICAL' ? '0.2' : '0.08'})`}`,
              background: selectedWardId === ward.id ? `${accentColor}15` : 'transparent',
              color: selectedWardId === ward.id ? accentColor : ward.severity === 'CRITICAL' ? 'rgba(212,197,169,0.7)' : 'rgba(212,197,169,0.4)',
              fontSize: 10,
              fontFamily: 'monospace',
              cursor: 'pointer',
            }}
          >
            {ward.shortName}
            {ward.severity === 'CRITICAL' && (
              <span style={{ marginLeft: 4, fontSize: 8, color: '#e05a5a' }}>●</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── EMBED HEADER ─────────────────────────────────────────────────────────────
function EmbedHeader({
  city,
  ward,
  accentColor,
  compact,
}: {
  city: City;
  ward: Ward | null;
  accentColor: string;
  compact: boolean;
}) {
  return (
    <div style={{
      padding: compact ? '12px 16px' : '20px 20px 16px',
      borderBottom: `1px solid ${accentColor}20`,
      background: `${accentColor}06`,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 9, color: `${accentColor}60`, letterSpacing: '.2em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 4 }}>
            Muslim Resource Guide · The Haal Project
          </div>
          <div style={{ fontSize: compact ? 16 : 22, color: '#F5F0E8', fontFamily: 'monospace', fontWeight: 900, letterSpacing: '.05em', textTransform: 'uppercase' }}>
            {ward ? ward.name : city.name}
          </div>
          {!compact && (
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: `${accentColor}70`, marginTop: 2 }}>
              {city.arabicName}
            </div>
          )}
          {ward && (
            <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.5)', marginTop: 4, fontFamily: 'monospace' }}>
              {city.name} · {ward.severity} Priority
            </div>
          )}
        </div>
        {!compact && ward && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.entries(ward.indicators).slice(0, 3).map(([key, val]) => {
              const layer = DATA_LAYERS.find(l => l.key === key);
              if (!layer) return null;
              return (
                <div key={key} style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 4,
                  padding: '6px 10px',
                  textAlign: 'center',
                  minWidth: 60,
                }}>
                  <div style={{ fontSize: 14, color: '#F5F0E8', fontFamily: 'monospace', fontWeight: 700 }}>
                    {key === 'medianIncome' ? `$${Math.round((val as number) / 1000)}K` : `${val}%`}
                  </div>
                  <div style={{ fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', textTransform: 'uppercase' }}>
                    {layer.label}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {!compact && ward?.openLetterQuote && (
        <div style={{
          marginTop: 12,
          padding: '8px 12px',
          borderLeft: `2px solid ${accentColor}40`,
          fontSize: 11,
          color: 'rgba(212,197,169,0.55)',
          fontStyle: 'italic',
          lineHeight: 1.6,
        }}>
          "{ward.openLetterQuote}"
        </div>
      )}
    </div>
  );
}

// ─── AYAH BANNER ─────────────────────────────────────────────────────────────
function AyahBanner({ category, accentColor }: { category: string | null; accentColor: string }) {
  const cat = RESOURCE_CATEGORIES.find(c => c.key === category);
  if (!cat) {
    return (
      <div style={{
        padding: '12px 16px',
        background: `${accentColor}06`,
        borderBottom: `1px solid ${accentColor}15`,
        textAlign: 'center',
      }}>
        <div style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: `${accentColor}70`, direction: 'rtl', marginBottom: 4 }}>
          وَتَعَاوَنُوا عَلَى الْبِرِّ وَالتَّقْوَىٰ
        </div>
        <div style={{ fontSize: 10, color: 'rgba(212,197,169,0.45)', fontStyle: 'italic' }}>
          "And cooperate in righteousness and God-consciousness." — Quran 5:2
        </div>
      </div>
    );
  }
  return (
    <div style={{
      padding: '12px 16px',
      background: `${accentColor}06`,
      borderBottom: `1px solid ${accentColor}15`,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: `${accentColor}60`, letterSpacing: '.15em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 4 }}>
            {cat.nameOfAllah} · {cat.arabic} · {cat.meaning}
          </div>
          <div style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: `${accentColor}80`, direction: 'rtl', marginBottom: 4 }}>
            {cat.ayat.arabic}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(212,197,169,0.6)', fontStyle: 'italic', lineHeight: 1.6 }}>
            "{cat.ayat.text}" — Quran {cat.ayat.ref}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CITY SELECTOR ────────────────────────────────────────────────────────────
function CitySelector({
  activeCityId,
  onChange,
  accentColor,
}: {
  activeCityId: string;
  onChange: (id: string) => void;
  accentColor: string;
}) {
  return (
    <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.12em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: 8 }}>
        Select City
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {CITIES.map(city => (
          <button
            key={city.id}
            onClick={() => onChange(city.id)}
            style={{
              padding: '5px 12px',
              borderRadius: 4,
              border: `1px solid ${activeCityId === city.id ? accentColor : 'rgba(255,255,255,0.1)'}`,
              background: activeCityId === city.id ? `${accentColor}15` : 'transparent',
              color: activeCityId === city.id ? accentColor : 'rgba(212,197,169,0.5)',
              fontSize: 10,
              fontFamily: 'monospace',
              cursor: 'pointer',
            }}
          >
            {city.name}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN EMBED WIDGET ────────────────────────────────────────────────────────
function MRGEmbedWidget() {
  const searchParams = useSearchParams();

  // Read URL params
  const paramCity = searchParams.get('city') || 'atlanta';
  const paramWard = searchParams.get('ward');
  const paramTheme = (searchParams.get('theme') as 'dark' | 'light' | 'forest') || 'dark';
  const paramCompact = searchParams.get('compact') === 'true';
  const paramTab = searchParams.get('tab') || 'resources';

  const [activeCityId, setActiveCityId] = useState(paramCity);
  const [selectedWardId, setSelectedWardId] = useState<string | null>(paramWard);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeTabLocal, setActiveTabLocal] = useState<'resources' | 'disparities' | 'context'>(
    paramTab as 'resources' | 'disparities' | 'context'
  );

  // Update city/ward when URL params change
  useEffect(() => {
    setActiveCityId(paramCity);
    setSelectedWardId(paramWard);
  }, [paramCity, paramWard]);

  const city = useMemo(() => CITIES.find(c => c.id === activeCityId) ?? CITIES[0], [activeCityId]);
  const ward = useMemo(() => city.wards.find(w => w.id === selectedWardId) ?? null, [city, selectedWardId]);

  const accentColor = '#c9a84c';

  // Collect all resources for city or ward
  const allResources = useMemo(() => {
    if (ward) return ward.resources;
    return city.wards.flatMap(w => w.resources);
  }, [city, ward]);

  const filteredResources = useMemo(() => {
    if (!activeCategory) return allResources;
    return allResources.filter(r => r.category === activeCategory);
  }, [allResources, activeCategory]);

  const allDisparities = useMemo(() => {
    if (ward) return ward.disparities;
    return city.wards.flatMap(w => w.disparities);
  }, [city, ward]);

  const bg = paramTheme === 'light' ? '#f5f0e8' : paramTheme === 'forest' ? '#061a0e' : '#060d12';
  const textColor = paramTheme === 'light' ? '#1a1a0e' : '#d4c5a9';

  return (
    <div style={{
      minHeight: '100vh',
      background: bg,
      color: textColor,
      fontFamily: 'monospace',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Amiri:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        button { font-family: monospace; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(201,168,76,0.2); border-radius: 2px; }
      `}</style>

      {/* City selector (shown when no city locked via param) */}
      {!searchParams.get('city') && (
        <CitySelector activeCityId={activeCityId} onChange={id => { setActiveCityId(id); setSelectedWardId(null); }} accentColor={accentColor} />
      )}

      {/* Header */}
      <EmbedHeader city={city} ward={ward} accentColor={accentColor} compact={paramCompact} />

      {/* Ward selector (shown when no ward locked via param) */}
      {!searchParams.get('ward') && (
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <WardSelector city={city} selectedWardId={selectedWardId} onSelect={setSelectedWardId} accentColor={accentColor} />
        </div>
      )}

      {/* Tab nav */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '0 16px' }}>
        {(['resources', 'disparities', 'context'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTabLocal(tab)}
            style={{
              padding: '10px 14px',
              fontSize: 9,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${activeTabLocal === tab ? accentColor : 'transparent'}`,
              color: activeTabLocal === tab ? accentColor : 'rgba(212,197,169,0.4)',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Ayah banner */}
      <AyahBanner category={activeCategory} accentColor={accentColor} />

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>

        {activeTabLocal === 'resources' && (
          <>
            <CategoryFilter active={activeCategory} onChange={setActiveCategory} accentColor={accentColor} />
            {filteredResources.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(212,197,169,0.3)', fontSize: 12 }}>
                <div style={{ fontSize: 24, marginBottom: 12 }}>🌱</div>
                <div>No resources listed yet for this area.</div>
                <div style={{ marginTop: 8, fontSize: 10 }}>
                  <a href="https://haalproject.org/muslim-resource-guide" target="_blank" rel="noopener noreferrer" style={{ color: accentColor, textDecoration: 'none' }}>
                    Submit a resource → haalproject.org
                  </a>
                </div>
              </div>
            ) : (
              filteredResources.map((resource, i) => (
                <ResourceCard key={i} resource={resource} accentColor={accentColor} />
              ))
            )}
          </>
        )}

        {activeTabLocal === 'disparities' && (
          <>
            <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 12 }}>
              Structural Disparities · Islah Calls
            </div>
            {allDisparities.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(212,197,169,0.3)', fontSize: 12 }}>
                No disparity data available for this selection.
              </div>
            ) : (
              allDisparities.map((d, i) => (
                <DisparityCard key={i} disparity={d} accentColor={accentColor} />
              ))
            )}
          </>
        )}

        {activeTabLocal === 'context' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* City description */}
            <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6 }}>
              <div style={{ fontSize: 9, color: `${accentColor}60`, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
                About {city.name}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(212,197,169,0.7)', lineHeight: 1.7 }}>{city.description}</div>
            </div>

            {/* Ward history */}
            {ward?.history && (
              <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6 }}>
                <div style={{ fontSize: 9, color: `${accentColor}60`, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
                  {ward.name} — History
                </div>
                <div style={{ fontSize: 12, color: 'rgba(212,197,169,0.7)', lineHeight: 1.7 }}>{ward.history}</div>
              </div>
            )}

            {/* Ward field notes */}
            {ward?.fieldNotes && (
              <div style={{ padding: '14px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 6 }}>
                <div style={{ fontSize: 9, color: 'rgba(45,122,71,0.7)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
                  Field Notes
                </div>
                <div style={{ fontSize: 12, color: 'rgba(212,197,169,0.7)', lineHeight: 1.7, fontStyle: 'italic' }}>{ward.fieldNotes}</div>
              </div>
            )}

            {/* Pivotal moments */}
            {ward?.pivotalMoments && ward.pivotalMoments.length > 0 && (
              <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6 }}>
                <div style={{ fontSize: 9, color: `${accentColor}60`, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 10 }}>
                  Pivotal Moments
                </div>
                {ward.pivotalMoments.map((m, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                    <span style={{ fontSize: 10, color: accentColor, fontFamily: 'monospace', flexShrink: 0, minWidth: 36 }}>{m.year}</span>
                    <span style={{ fontSize: 11, color: 'rgba(212,197,169,0.65)', lineHeight: 1.5 }}>{m.event}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Islah call */}
            <div style={{ padding: '14px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderLeft: '3px solid rgba(45,122,71,0.5)', borderRadius: 4 }}>
              <div style={{ fontSize: 9, color: 'rgba(45,122,71,0.7)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6 }}>
                Islah · إِصْلَاح
              </div>
              <div style={{ fontSize: 12, color: 'rgba(212,197,169,0.7)', lineHeight: 1.7, fontStyle: 'italic' }}>
                "The word and work we're looking for is Islah — to cultivate, mend, restore, renew, work righteousness. Every resource in this guide is a point of Uboor — a bridge from disparity to provision." — The Haal Project Open Letter
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Backlink footer */}
      <BacklinkFooter city={activeCityId} ward={selectedWardId} />
    </div>
  );
}

// ─── PAGE WRAPPER ─────────────────────────────────────────────────────────────
export default function MRGEmbedPage() {
  return (
    <Suspense fallback={
      <div style={{ minHeight: '100vh', background: '#060d12', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'rgba(201,168,76,0.5)', fontFamily: 'monospace', fontSize: 11, letterSpacing: '.15em' }}>
          Loading Muslim Resource Guide...
        </div>
      </div>
    }>
      <MRGEmbedWidget />
    </Suspense>
  );
}
